import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { CheckCircle, ArrowRight, Zap, Crown, Lock } from 'lucide-react';

export default function RadarPage() {
  const [plan, setPlan] = useState('monthly');

  const features_free = [
    'Gratis online groeiscan',
    'Persoonlijk dashboard',
    'Groeiscore (0-100)',
    '3 kritieke aandachtspunten',
    '3 quick wins',
    '5 zoekwoorden tracking',
    'Google Bedrijfsprofiel check',
    'Bedrijfsgidsen check',
  ];

  const features_radar = [
    'Alles van Gratis, plus:',
    'Volledige website-analyse',
    'Complete SEO & zoekwoordenanalyse',
    'Lokale zichtbaarheid & directories',
    'Review monitoring & reputatiescore',
    'Concurrentiebenchmark',
    'Maandelijkse voortgangsrapportage',
    'Stap-voor-stap verbeterinstructies',
    'Uitvoering aanvragen bij IMPERIA AI',
  ];

  const features_360 = [
    'Alles van IMPERIA AI Radar, plus:',
    'Dedicated marketing specialist',
    'Maandelijkse strategy call',
    'Uitvoering van verbeteringen',
    'Google Ads beheer',
    'SEO uitvoering',
    'Social media management',
    'Volledige marketing ontzorging',
  ];

  return (
    <>
      <SEOHead
        title="IMPERIA AI Radar — Volledige Online Marketing Monitoring"
        description="Ontgrendel alle analyses en inzichten voor uw bedrijf met IMPERIA AI Radar. Vanaf €14,95 per maand."
        canonical="/radar"
      />

      {/* Hero */}
      <section className="bg-[#0f172a] pt-28 pb-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4A017] via-[#f5c842] to-[#D4A017]" />
        <div className="container-custom text-center">
          <span className="inline-block bg-[#D4A017]/15 text-[#D4A017] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
            IMPERIA AI Radar
          </span>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-5">
            Transparante prijzen, volledige inzichten
          </h1>
          <p className="text-[#94a3b8] text-lg max-w-2xl mx-auto mb-8">
            Start gratis met een scan. Ontgrendel daarna alles met IMPERIA AI Radar voor een eerlijke prijs.
          </p>

          {/* Toggle */}
          <div className="inline-flex bg-white/5 rounded-xl p-1 mb-12">
            {[['monthly', 'Maandelijks'], ['yearly', 'Jaarlijks (bespaar €20)']].map(([v, l]) => (
              <button key={v} onClick={() => setPlan(v)}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  plan === v ? 'bg-[#D4A017] text-[#0f172a]' : 'text-[#94a3b8] hover:text-white'
                }`}>{l}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="bg-[#0a0f1e] py-16">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">

            {/* Free */}
            <div className="bg-[#0f172a] rounded-2xl border border-white/10 p-8">
              <p className="text-[#64748b] font-bold text-sm uppercase tracking-wider mb-3">Gratis</p>
              <p className="text-4xl font-bold text-white mb-1">€0</p>
              <p className="text-[#64748b] text-sm mb-6">Altijd gratis · Geen creditcard</p>
              <Link to="/register" className="block w-full text-center bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-xl mb-6 transition-all">
                Gratis scan starten
              </Link>
              <ul className="space-y-2.5">
                {features_free.map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-[#94a3b8]">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Radar */}
            <div className="bg-[#0f172a] rounded-2xl border-2 border-[#D4A017] p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A017] text-[#0f172a] text-xs font-bold px-4 py-1 rounded-full">
                MEEST GEKOZEN
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-5 h-5 text-[#D4A017]" />
                <p className="text-[#D4A017] font-bold text-sm uppercase tracking-wider">IMPERIA AI Radar</p>
              </div>
              <p className="text-4xl font-bold text-white mb-1">
                {plan === 'monthly' ? '€14,95' : '€99,95'}
              </p>
              <p className="text-[#64748b] text-sm mb-6">
                {plan === 'monthly' ? 'per maand · Opzegbaar' : 'per jaar · Bespaar €20'}
              </p>
              <Link to="/register" className="block w-full text-center bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold py-3 rounded-xl mb-6 transition-all">
                Start met gratis scan <ArrowRight className="inline ml-1" size={14} />
              </Link>
              <ul className="space-y-2.5">
                {features_radar.map((f, i) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${i === 0 ? 'text-[#D4A017] font-semibold' : 'text-[#94a3b8]'}`}>
                    {i > 0 && <CheckCircle className="w-4 h-4 text-[#D4A017] flex-shrink-0 mt-0.5" />}
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* 360 */}
            <div className="bg-[#0f172a] rounded-2xl border border-white/10 p-8">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-purple-400" />
                <p className="text-purple-400 font-bold text-sm uppercase tracking-wider">IMPERIA AI 360</p>
              </div>
              <p className="text-4xl font-bold text-white mb-1">Op maat</p>
              <p className="text-[#64748b] text-sm mb-6">Volledig ontzorgd · Altijd een specialist</p>
              <Link to="/offerte" className="block w-full text-center bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-xl mb-6 transition-all">
                Offerte aanvragen
              </Link>
              <ul className="space-y-2.5">
                {features_360.map((f, i) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${i === 0 ? 'text-purple-400 font-semibold' : 'text-[#94a3b8]'}`}>
                    {i > 0 && <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />}
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0f172a] py-16">
        <div className="container-custom max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl font-bold text-white text-center mb-10">Veelgestelde vragen</h2>
          <div className="space-y-4">
            {[
              ['Hoe werkt de gratis scan?', 'U vult uw bedrijfsgegevens in, wij analyseren uw website en online aanwezigheid met AI en geven u direct inzicht in uw groeiscore, kritieke punten en quick wins. U ontvangt ook direct een gratis dashboard.'],
              ['Wat is het verschil tussen gratis en Radar?', 'De gratis versie toont uw groeiscore, 3 kritieke aandachtspunten, 3 quick wins, 5 zoekwoorden tracking, een Google Bedrijfsprofiel check en een bedrijfsgidsen check. IMPERIA AI Radar ontgrendelt alle analyses, stap-voor-stap instructies, maandelijkse monitoring en de mogelijkheid om verbeteringen door IMPERIA AI te laten uitvoeren.'],
              ['Kan ik op elk moment opzeggen?', 'Ja. IMPERIA AI Radar heeft geen lange contracten. U kunt maandelijks opzeggen.'],
              ['Hoe worden de scans gemaakt?', 'Wij combineren basis website-analyse met persoonlijke inzichten op basis van uw branche, locatie en doelen. Hierdoor krijgt u een persoonlijk en relevant rapport.'],
              ['Kan IMPERIA AI ook alles uitvoeren?', 'Ja. Via IMPERIA AI 360 voeren onze specialisten alle verbeteringen voor u uit. Van SEO tot Google Ads — volledig ontzorgd.'],
            ].map(([q, a]) => (
              <div key={q} className="bg-white/5 rounded-xl p-5">
                <p className="text-white font-semibold mb-2">{q}</p>
                <p className="text-[#64748b] text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
